import type {AiChatStateContextType} from '@AiChat/providers/ai-chat-provider.types';
import {InspirationLayoutVariant, InspirationWidthVariant} from '@AiChat/components/ai-chat-window-inspirations';
import {ButtonType} from '@Components/button';

export const AI_CHAT_SEASON_SUGGESTIONS_PER_SET = 3;
export const AI_CHAT_SEASON_MOBILE_INSPIRATION_COUNT = 3;

export enum AiChatSeason {
  HALLOWEEN = 'HALLOWEEN',
}

export enum AiChatHalloweenAsset {
  SPIDER_WEB = 'ai-chat-halloween-spider-web.webp',
  SPIDER = 'ai-chat-halloween-spider.webp',
  PUMPKIN_LANTERN = 'ai-chat-halloween-pumpkin-lantern.webp',
  PUMPKIN_ANGRY = 'ai-chat-halloween-pumpkin-angry.webp',
  PUMPKIN_HAPPY = 'ai-chat-halloween-pumpkin-happy.webp',
}

enum AiChatHalloweenCard {
  BAKERY_MENU = 'ai-chat-halloween-card-bakery-menu.webp',
  PARTY_POSTER = 'ai-chat-halloween-card-party-poster.webp',
  PROMO_IDEAS = 'ai-chat-halloween-card-promo-ideas.webp',
  CAPTION = 'ai-chat-halloween-card-caption.webp',
}

export type AiChatEmptyHomeState = Pick<AiChatStateContextType, 'currentChatId' | 'isQuickTipsView' | 'isAssetsView' | 'isMemoryView'>;

export type AiChatSeasonPageState = AiChatEmptyHomeState & Pick<AiChatStateContextType, 'isEmbedded'>;

export interface AiChatSeasonDates {
  startMonthIndex: number;
  startDay: number;
  endMonthIndex: number;
  endDay: number;
}

export interface AiChatSeasonSuggestion {
  emoji: string;
  titleKey: string;
  promptKey: string;
}

export interface AiChatSeasonInspiration {
  card: string;
  visualLayoutVariant: InspirationLayoutVariant;
  widthVariant: InspirationWidthVariant;
  pillTextKey: string;
  pillIcon: string;
  promptKey: string;
}

export interface AiChatSeasonConfig {
  dates: AiChatSeasonDates;
  suggestions: AiChatSeasonSuggestion[];
  inspirations: AiChatSeasonInspiration[];
  loginButtonType: ButtonType;
}

const AI_CHAT_HALLOWEEN_SUGGESTIONS: AiChatSeasonSuggestion[] = [
  {emoji: '🎃', titleKey: 'pmwjs_ai_chat_halloween_suggestion_costume_party_poster', promptKey: 'pmwjs_ai_chat_halloween_suggestion_costume_party_poster_prompt'},
  {emoji: '👻', titleKey: 'pmwjs_ai_chat_halloween_suggestion_cute_ghost_sticker', promptKey: 'pmwjs_ai_chat_halloween_suggestion_cute_ghost_sticker_prompt'},
  {emoji: '💡', titleKey: 'pmwjs_ai_chat_halloween_suggestion_promo_ideas', promptKey: 'pmwjs_ai_chat_halloween_suggestion_promo_ideas_prompt'},
  {emoji: '🦇', titleKey: 'pmwjs_ai_chat_halloween_suggestion_haunted_house_flyer', promptKey: 'pmwjs_ai_chat_halloween_suggestion_haunted_house_flyer_prompt'},
  {emoji: '🎃', titleKey: 'pmwjs_ai_chat_halloween_suggestion_pumpkin_logo', promptKey: 'pmwjs_ai_chat_halloween_suggestion_pumpkin_logo_prompt'},
  {emoji: '✍️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_instagram_caption', promptKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_instagram_caption_prompt'},
  {emoji: '🍬', titleKey: 'pmwjs_ai_chat_halloween_suggestion_trick_or_treat_poster', promptKey: 'pmwjs_ai_chat_halloween_suggestion_trick_or_treat_poster_prompt'},
  {emoji: '🕯️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_cafe_photo', promptKey: 'pmwjs_ai_chat_halloween_suggestion_spooky_cafe_photo_prompt'},
  {emoji: '💡', titleKey: 'pmwjs_ai_chat_halloween_suggestion_sale_ideas', promptKey: 'pmwjs_ai_chat_halloween_suggestion_sale_ideas_prompt'},
  {emoji: '🛍️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_sale_post', promptKey: 'pmwjs_ai_chat_halloween_suggestion_sale_post_prompt'},
  {emoji: '🧙', titleKey: 'pmwjs_ai_chat_halloween_suggestion_witch_hat_lettering', promptKey: 'pmwjs_ai_chat_halloween_suggestion_witch_hat_lettering_prompt'},
  {emoji: '✍️', titleKey: 'pmwjs_ai_chat_halloween_suggestion_costume_contest_caption', promptKey: 'pmwjs_ai_chat_halloween_suggestion_costume_contest_caption_prompt'},
];

const AI_CHAT_HALLOWEEN_INSPIRATIONS: AiChatSeasonInspiration[] = [
  {card: AiChatHalloweenCard.BAKERY_MENU, visualLayoutVariant: InspirationLayoutVariant.Centered, widthVariant: InspirationWidthVariant.Medium, pillTextKey: 'pmwjs_ai_chat_inspiration_design', pillIcon: 'icon-designs', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_1'},
  {card: AiChatHalloweenCard.PARTY_POSTER, visualLayoutVariant: InspirationLayoutVariant.Centered, widthVariant: InspirationWidthVariant.Medium, pillTextKey: 'pmwjs_ai_chat_inspiration_images', pillIcon: 'icon-image', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_2'},
  {card: AiChatHalloweenCard.PROMO_IDEAS, visualLayoutVariant: InspirationLayoutVariant.FullWidth, widthVariant: InspirationWidthVariant.Narrow, pillTextKey: 'pmwjs_ai_chat_inspiration_marketing_ideas', pillIcon: 'icon-megaphone', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_3'},
  {card: AiChatHalloweenCard.CAPTION, visualLayoutVariant: InspirationLayoutVariant.FullWidth, widthVariant: InspirationWidthVariant.Narrow, pillTextKey: 'pmwjs_ai_chat_inspiration_captions', pillIcon: 'icon-ai-caption', promptKey: 'pmwjs_ai_chat_halloween_inspiration_prompt_4'},
];

export const AI_CHAT_SEASON_CONFIGS: Record<AiChatSeason, AiChatSeasonConfig> = {
  [AiChatSeason.HALLOWEEN]: {
    dates: {startMonthIndex: 9, startDay: 1, endMonthIndex: 9, endDay: 31},
    suggestions: AI_CHAT_HALLOWEEN_SUGGESTIONS,
    inspirations: AI_CHAT_HALLOWEEN_INSPIRATIONS,
    loginButtonType: ButtonType.SECONDARY,
  },
};
